import React from 'react';

import { NavLinkButton, Table } from '@shared/components/bootstrap';
import { Timestamp } from '@shared/components/helpers';
import { useDebounce } from '@shared/hooks';
import { useBooleanURLSearchParam } from '@shared/hooks/use_boolean_url_search_param';
import { useNumericURLSearchParam } from '@shared/hooks/use_numeric_url_search_param';
import { useURLSearchParam } from '@shared/hooks/use_url_search_param';

import { Panel } from '@admin/components/helpers/panel';
import { Permission } from '@admin/components/helpers/permission';
import { Pagination } from '@admin/components/pagination';
import { Spinner } from '@admin/components/spinner';
import { useTitle } from '@admin/hooks/use_title';
import { CLAIMS } from '@admin/libraries/access_control_catalog.generated';
import { client } from '@admin/libraries/apollo';
import { usePaginatedClaimsQuery, type Claim__Status } from '@admin/schema';
import { Claim__FiltersInput } from '@admin/schema';

import { Filters } from './filters';

const formatDate = (time?: string) => (time ? <Timestamp value={time} format={Timestamp.Format.Date} /> : 'N/A');

const DashboardContent: React.FC = () => {
  useTitle('Claims');
  const [page, setPage] = useNumericURLSearchParam('page');
  const [status, setStatus] = useURLSearchParam<Claim__Status>('status');
  const [ownerID, setOwnerID] = useURLSearchParam('owner_id');
  const [hasDamagedItem, setHasDamagedItem] = useBooleanURLSearchParam('has_damaged_item');
  const [hasDamagedProperty, setHasDamagedProperty] = useBooleanURLSearchParam('has_damaged_property');
  const [hasLostItem, setHasLostItem] = useBooleanURLSearchParam('has_lost_item');
  const [hasIssue, setHasIssue] = useBooleanURLSearchParam('has_issue');
  const [query, setQuery] = useURLSearchParam('query');

  const { data, loading, error } = usePaginatedClaimsQuery({
    client,
    variables: {
      page,
      filters: {
        status: status,
        ownerID: ownerID,
        hasIssue: hasIssue,
        hasDamagedItem: hasDamagedItem,
        hasDamagedProperty: hasDamagedProperty,
        hasLostItem: hasLostItem,
        query: useDebounce(query),
      },
    },
  });

  const claims = data && data.paginatedClaims;

  if (error) {
    return <div>There was an error loading: {error.message}</div>;
  }

  return (
    <>
      <Filters
        query={query}
        status={status}
        ownerID={ownerID}
        hasDamagedItem={hasDamagedItem}
        hasDamagedProperty={hasDamagedProperty}
        hasLostItem={hasLostItem}
        hasIssue={hasIssue}
        onQuery={setQuery}
        onStatus={setStatus}
        onOwnerID={setOwnerID}
        onHasDamagedItem={setHasDamagedItem}
        onHasDamagedProperty={setHasDamagedProperty}
        onHasLostItem={setHasLostItem}
        onHasIssue={setHasIssue}
      />
      {loading ? (
        <Spinner />
      ) : (
        <Panel>
          <Panel.Header>
            <Panel.Title>Claims</Panel.Title>
          </Panel.Header>
          <Panel.Body>
            <Table striped responsive>
              <thead>
                <tr>
                  <th>Number</th>
                  <th>Opened On</th>
                  <th>Customer Name</th>
                  <th>Kind</th>
                  <th>Owner</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {claims &&
                  claims.results.map((claim) => (
                    <tr key={claim.number}>
                      <td>{claim.number}</td>
                      <td>{formatDate(claim.openedAt ?? undefined)}</td>
                      <td>{claim.account.customer.name}</td>
                      <td>{claim.kind}</td>
                      <td>{claim.owner ? claim.owner.name : 'Unassigned'}</td>
                      <td>{claim.status.toLowerCase()}</td>
                      <td>
                        <NavLinkButton to={`/claims/${claim.id}`} kind="primary">
                          View
                        </NavLinkButton>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </Table>
            {claims && <Pagination onPage={setPage} pagination={claims.pagination} />}
          </Panel.Body>
        </Panel>
      )}
    </>
  );
};

export const Dashboard: React.FC = () => (
  <Permission resource={CLAIMS.resource} action={CLAIMS.actions.read}>
    <DashboardContent />
  </Permission>
);
